import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Plus } from "lucide-react";

export default function SchoolPage() {
  const { user, refresh } = useAuth();
  const [schools, setSchools] = useState([]);
  const [currentId, setCurrentId] = useState("");
  const [form, setForm] = useState({});
  const [users, setUsers] = useState([]);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([api.get("/schools"), user.role === "admin" ? api.get("/users") : Promise.resolve({ data: [] })]);
      setSchools(s.data);
      setUsers(u.data);
      const initial = user.school_id || (s.data[0]?.id ?? "");
      if (initial) {
        setCurrentId(initial);
        const sc = s.data.find((x) => x.id === initial);
        if (sc) setForm(sc);
      }
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (currentId) {
      const sc = schools.find((s) => s.id === currentId);
      if (sc) setForm(sc);
    }
  }, [currentId, schools]);

  const save = async () => {
    if (!currentId) {
      // create
      try {
        const { data } = await api.post("/schools", form);
        toast.success("École créée");
        await refresh();
        setSchools([...schools, data]);
        setCurrentId(data.id);
      } catch (e) { toast.error(formatError(e)); }
      return;
    }
    try {
      await api.patch(`/schools/${currentId}`, form);
      toast.success("Modifications enregistrées");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const createNew = () => {
    setCurrentId("");
    setForm({ nom: "", ia: "", ief: "", annee_scolaire: "2025-2026", logo_url: "", signature_directeur_url: "", signature_maitre_url: "" });
  };

  const directeurs = users.filter((u) => u.role === "directeur");

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Mon école</h1>
          <p className="text-sm text-[#5C736A]">Identité, année scolaire et signatures</p>
        </div>
        <div className="flex items-center gap-2">
          {schools.length > 0 && user.role === "admin" && (
            <Select value={currentId} onValueChange={setCurrentId}>
              <SelectTrigger className="w-64" data-testid="select-school"><SelectValue placeholder="Choisir une école" /></SelectTrigger>
              <SelectContent>
                {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {user.role === "admin" && (
            <Button onClick={createNew} variant="outline" data-testid="new-school-btn"><Plus className="w-4 h-4 mr-1" /> Nouvelle école</Button>
          )}
        </div>
      </div>

      <div className="ek-card p-6 space-y-4 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nom de l&apos;école</Label>
            <Input data-testid="school-nom" value={form.nom || ""} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
          </div>
          <div>
            <Label>Année scolaire</Label>
            <Input data-testid="school-annee" value={form.annee_scolaire || ""} onChange={(e) => setForm({ ...form, annee_scolaire: e.target.value })} />
          </div>
          <div>
            <Label>IA (Inspection Académique)</Label>
            <Input data-testid="school-ia" value={form.ia || ""} onChange={(e) => setForm({ ...form, ia: e.target.value })} />
          </div>
          <div>
            <Label>IEF</Label>
            <Input data-testid="school-ief" value={form.ief || ""} onChange={(e) => setForm({ ...form, ief: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>URL du logo (optionnel)</Label>
            <Input data-testid="school-logo" value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." />
          </div>
          <div>
            <Label>URL signature directeur</Label>
            <Input value={form.signature_directeur_url || ""} onChange={(e) => setForm({ ...form, signature_directeur_url: e.target.value })} />
          </div>
          <div>
            <Label>URL signature maître</Label>
            <Input value={form.signature_maitre_url || ""} onChange={(e) => setForm({ ...form, signature_maitre_url: e.target.value })} />
          </div>
          {user.role === "admin" && (
            <div className="md:col-span-2">
              <Label>Directeur</Label>
              <Select value={form.directeur_id || ""} onValueChange={(v) => setForm({ ...form, directeur_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choisir un directeur" /></SelectTrigger>
                <SelectContent>
                  {directeurs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({d.username})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <Button onClick={save} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="save-school-btn">
          <Save className="w-4 h-4 mr-2" /> Enregistrer
        </Button>
      </div>
    </div>
  );
}
